import { FormEvent, useEffect, useState } from 'react';
import { ApiError, apiClient } from '../../lib/api';
import { Button } from '../../components/Button';
import { toast } from '../../lib/toast';
import type { OnboardingState } from '../../lib/types';
import styles from './Onboarding.module.css';

export function OnboardingPage() {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const next = await apiClient.get<OnboardingState>('/api/v1/onboarding/state');
      setState(next);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No active session yet — start one.
        try {
          const next = await apiClient.post<OnboardingState>('/api/v1/onboarding/start');
          setState(next);
        } catch {
          toast.error('Не удалось начать онбординг.');
        }
      }
      // 401 is handled in apiClient.
    }
  }

  async function answer(e: FormEvent) {
    e.preventDefault();
    if (!state || !draft.trim()) return;
    setPending(true);
    try {
      const next = await apiClient.post<OnboardingState>('/api/v1/onboarding/answer', {
        question_id: state.question.id,
        content: draft.trim(),
      });
      setState(next);
      setDraft('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Не удалось отправить ответ.');
    } finally {
      setPending(false);
    }
  }

  if (!state) {
    return (
      <main className={styles.stage}>
        <p className={styles.loading}>Открываем мастерскую…</p>
      </main>
    );
  }

  const { question, history, goals_so_far, archetypes, target_count } = state;

  return (
    <main className={styles.stage}>
      <header className={styles.head}>
        <div className={styles.left}>Сессия {state.session_index} из 2</div>
        <div className={styles.center}>
          Мастерская <span>·</span> сборка картотеки
        </div>
        <div className={styles.right}>
          Вопрос {question.number} из {question.total}
        </div>
      </header>

      <div className={styles.progressRail}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${(question.number / Math.max(question.total, 1)) * 100}%` }}
          />
          <div
            className={styles.progressMarker}
            style={{ left: `${(question.number / Math.max(question.total, 1)) * 100}%` }}
          />
        </div>
        <span className={styles.progressText}>
          <b>{question.number}</b> / {question.total}
        </span>
      </div>

      <div className={styles.talk}>
        <section className={styles.dialogue}>
          {history.map((turn, i) => (
            <article key={i} className={styles.turn}>
              <div
                className={`${styles.turnLabel} ${turn.role === 'user' ? styles.user : ''}`}
              >
                <span>{turn.role === 'user' ? 'Вы' : 'Мастер'}</span>
                <span className={styles.when}>{formatTime(turn.at)}</span>
              </div>
              {turn.role === 'user' ? (
                <p className={styles.userText}>{turn.content}</p>
              ) : (
                <p className={styles.aiText}>{turn.content}</p>
              )}
            </article>
          ))}

          <div className={styles.currentQuestion}>
            <div className={styles.questionEyebrow}>
              <span>Текущий вопрос</span>
              <span className={styles.num}>№ {question.number}</span>
            </div>
            <p className={styles.questionText}>{question.prompt}</p>
            {question.aside && <p className={styles.questionAside}>{question.aside}</p>}
          </div>

          <form onSubmit={answer} className={styles.composer}>
            <div className={styles.composerHead}>
              <span className={styles.composerLabel}>Ваш ответ</span>
              <span className={styles.composerHint}>не торопитесь — это разговор</span>
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              placeholder="Напишите столько, сколько потребуется."
              rows={5}
            />
            <div className={styles.composerActions}>
              <Button type="submit" disabled={pending || !draft.trim()} trailing="→">
                {pending ? 'Принимаем' : 'отправить ответ'}
              </Button>
            </div>
          </form>
        </section>

        <aside className={styles.synthesis}>
          <div className={styles.synthHead}>
            <h2>
              Картотека <em>сессии</em>
            </h2>
            <p className={styles.synthSub}>что собирается, пока мы говорим</p>
          </div>

          <div className={styles.synthBlock}>
            <div className={styles.synthLabel}>
              <span>Цели</span>
              <span className={styles.count}>{goals_so_far.length}</span>
            </div>
            {goals_so_far.length === 0 ? (
              <p className={styles.empty}>пока пусто</p>
            ) : (
              goals_so_far.map((g) => (
                <div key={g.id} className={styles.goalCard}>
                  <div className={styles.priority}>приоритет {g.priority}</div>
                  <div className={styles.goalTitle}>{g.title}</div>
                  {g.deadline && <div className={styles.goalMeta}>срок · {g.deadline}</div>}
                </div>
              ))
            )}
          </div>

          <div className={styles.synthBlock}>
            <div className={styles.synthLabel}>
              <span>Архетипы</span>
              <span className={styles.count}>{archetypes.length}</span>
            </div>
            <div className={styles.archetypes}>
              {archetypes.map((a) => (
                <div key={a.num} className={styles.archetype}>
                  <span className={styles.aNum}>№ {a.num}</span>
                  <span className={styles.aName}>
                    {a.name}
                    {a.tagline && <em>{a.tagline}</em>}
                  </span>
                  <span
                    className={`${styles.aWeight} ${a.weight ? styles[`w_${a.weight}`] : ''}`}
                  >
                    {a.weight ?? ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.synthBlock}>
            <div className={styles.synthLabel}>
              <span>Партнёры — заготовки</span>
              <span className={styles.count}>{target_count}</span>
            </div>
            <p className={styles.placeholders}>
              По итогам сессии будут предложены <b>{target_count}</b> кандидатов для
              картотеки. Их можно будет отредактировать перед сохранением.
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}
